import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import find from 'lodash/find';
import each from 'lodash/each';
import findIndex from 'lodash/findIndex';
import {
    CREDIT_CARD_FORM_CONFIG_ERROR_MSG_FIELD_REQUIRED,
    CREDIT_CARD_FORM_CONFIG_ERROR_MSG_UNKNOWN_TYPE,
    paymentMethodIconMap,
} from 'yoda-checkout-components/lib/common/Constants';
import { findCardType } from 'yoda-site-components/lib/utils';
import { ERRORS } from 'yoda-checkout-components/lib/common/LabelConstants';
import User from 'yoda-site-components/lib/helpers/User/User';
// eslint-disable-next-line import/no-cycle
import ShippingService from '../../services/ShippingService';

export const TRANSIENT_PAYMENT_ID = '-1';

export function getProductPPIds(items = []) {
    const productIds = items.map((item) => item.product.id);
    return productIds.join(',');
}

export function getAppliedAppGCList(appGCList, appliedGClist = []) {
    appGCList.forEach((appGCItem) => {
        const { giftCardNumber } = appGCItem;
        const slicedGiftCardNumber = giftCardNumber.slice(giftCardNumber.length - 4);
        if (appliedGClist.length > 0) {
            appliedGClist.forEach((appliedGCItem) => {
                const { number, id } = appliedGCItem;
                if (number === slicedGiftCardNumber) {
                    appGCItem.selected = true;
                    appGCItem.id = id;
                    return appGCItem;
                }
            });
        } else {
            appGCItem.selected = false;
        }
    });
    return appGCList;
}

/**
 * helper function to return card type object's information such as maxLength,
 *     name of the card type, regex, label
 * @param {string} value - input value from credit card number input field
 * @return {object} card type object
 */
export function getCreditCardType(value, context) {
    const cardTypeEnum = findCardType(value, context);
    return cardTypeEnum;
}

/**
 * validate credit card field, if validation fails, return error msg
 * if cannot find valid cardtype, return unknownType error msg
 * for non-visa cards, check if input has enough card number length
 * for visa cards, check if input meets lowest card number length
 * is necessary when user input an unsupported card
 * @param {string} value - credit card number input field value
 * @return {string} string of error msg
 */
export function validateCreditCardNumber(value, context) {
    const cardType = getCreditCardType(value, context);
    // make sure when no value in input field, no error msg will show up
    if (value && cardType === null) {
        return CREDIT_CARD_FORM_CONFIG_ERROR_MSG_UNKNOWN_TYPE;
    }
    if (value && cardType && cardType.name === 'visa' && value.length < 13) {
        return 'Oops. We did not recognize the "type" of card you entered. Make sure you entered your "card number" correctly';
    }
    if (value && cardType && cardType.name !== 'visa' && value.length !== cardType.maxLength) {
        return 'Oops. We did not recognize the "type" of card you entered. Make sure you entered your "card number" correctly';
    }
    return undefined;
}

/**
 * validate no-empty field, if field is empty, return error msg, otherwise no msg
 * is necessary when user tried to submit an empty form
 * @param {string} value - field value
 * @return {string} string of error msg
 */
export function validateRequired(value, fieldName) {
    if (!value) {
        switch (fieldName) {
            case 'creditCardNumber':
                return 'Enter a credit card number';
            case 'creditCardCVV':
                return 'Please enter a valid card verification number';
            case 'creditCardMonth':
                return 'Select a valid month';
            case 'creditCardYear':
                return 'Select a valid year';
            case 'firstName':
                return 'Enter a first name';
            case 'lastName':
                return 'Enter a last name';
            case 'lineOne':
                return 'Enter a valid street address';
            case 'city':
                return 'Enter a city';
            case 'company':
                return 'Enter a valid company';
            case 'zip':
                return 'Enter a valid ZIP code';
            case 'state':
                return 'Select a state';
            case 'email':
                return 'Please enter a valid email address';
            case 'phone':
                return 'Enter a phone number';
            case 'militaryAddressType':
                return 'Select APO/FPO/DPO';
            case 'giftCardNumber':
                return 'Enter a valid gift card number';
            case 'securityCode':
                return 'Enter a security code';
            default:
                return CREDIT_CARD_FORM_CONFIG_ERROR_MSG_FIELD_REQUIRED;
        }
    }
    return undefined;
}

/**
 * validate cvv field, if validation fails, return error msg, otherwise no msg
 * for non-amex cards, check if cvv has enough digits
 * for amex card, both 3 and 4 digits are acceptable
 * is necessary when user input an invalid cvv number
 * @param {string} value - cvv input value
 * @param {string} cardNumber - credit card number input value
 * @return {string} string of error msg
 */
export function validateCVV(value, cardNumber, context, EditCardType) {
    let cardType = {};
    const fetchCardType = cardNumber && cardNumber.toString().substring(0, 4);
    if (fetchCardType === '****') {
        const { preferences: { creditCardConfig = {} } = {} } = context;
        const cardTypeName = find(creditCardConfig, {
            name: EditCardType,
        });
        /* istanbul ignore else */
        if (cardTypeName && cardTypeName.name && cardTypeName.name === EditCardType) {
            cardType = cardTypeName;
        }
    } else {
        cardType = getCreditCardType(cardNumber, context);
    }
    if (value && cardType && cardType.name === 'amex' && value.length < 3) {
        return `Must be at least 3 digits for ${cardType.label}`;
    }
    if (
        value &&
        !isEmpty(cardType) &&
        cardType.name !== 'amex' &&
        value.length !== cardType.securityCodeLength
    ) {
        return `Must be ${cardType.securityCodeLength} digits for ${cardType.label}`;
    }
    return undefined;
}

/**
 * validate no-empty EMAIL field, if field is empty or doesnt match regex email format, return error msg, otherwise no msg
 * is necessary when user tried to submit an empty form
 * @param {string} value - field value
 * @return {string} string of error msg
 */
export function validateEmail(value) {
    const emailPattern = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
    if (!value || !emailPattern.test(value) || value.length > 70) {
        return ERRORS.INVALID_EMAIL_MSG;
    }
    return undefined;
}

export function validateName(value, field) {
    let patternToValidate = /^(?!.*([A-Za-z0-9À-ÖØ-öø-ÿ ])\1{2})(?=.*[a-zA-ZÀ-ÖØ-öø-ÿ])[A-Za-z0-9À-ÖØ-öø-ÿ,. -]{1,17}$/;
    if (field === 'lastName') {
        patternToValidate = /^(?!.*([A-Za-z0-9À-ÖØ-öø-ÿ ])\1{3})(?=.*[a-zA-ZÀ-ÖØ-öø-ÿ])[A-Za-z0-9À-ÖØ-öø-ÿ,. -]{2,22}$/;
    }
    if (!value || !patternToValidate.test(value) || value.length > 17) {
        return `Enter a valid  ${field === 'lastName' ? 'last' : 'first'} name`;
    }
    return undefined;
}

export function validateZip(value, isZipInvalidFromAPI = false) {
    if (!value || value.length < 5 || isZipInvalidFromAPI) {
        return 'Enter a valid ZIP code';
    }
    return undefined;
}

export function validatePhoneNumber(value) {
    const pattern = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/i;
    if (!value || !pattern.test(value)) {
        return ERRORS.INVALID_PHONE_MSG;
    }
    return undefined;
}

export function getSkusFromDraftOrder(response) {
    const skus = [];
    if (
        response.data &&
        response.data.bag &&
        response.data.bag.items &&
        response.data.bag.items.length
    ) {
        for (let i = 0, count = response.data.bag.items.length; i < count; i += 1) {
            const item = response.data.bag.items[i];
            skus.push(item.sku.id);
        }
    }
    return skus.join(',');
}

export function mapCards(cardType) {
    let cardIcon = null;
    /* istanbul ignore else */
    if (!isEmpty(cardType)) {
        cardIcon = paymentMethodIconMap[cardType.toUpperCase()];
    }
    return cardIcon;
}

/**
 * Check whether AdBlock enabled on browser and return promise
 */
export function isAdBlockEnabledOnBrowser() {
    const adBlockTester = document.createElement('div');
    adBlockTester.innerHTML = '&nbsp;';
    adBlockTester.className = 'adsbox';
    adBlockTester.height = '1px';
    adBlockTester.width = '1px';
    document.body.appendChild(adBlockTester);
    const promise = new Promise((resolve) => {
        setTimeout(() => {
            resolve(adBlockTester.offsetHeight === 0);
        }, 1500);
    });
    return promise;
}

/*
 * Return error message based on credit card form input field
 */
export function validateCreditCardFormField(newPayment, fieldName, context) {
    const errorFieldMap = {
        number: 'creditCardNumber',
        expMonth: 'creditCardMonth',
        expYear: 'creditCardYear',
        cvv: 'creditCardCVV',
        emailId: 'email',
        phone: 'phone',
    };
    if (fieldName === 'emailId') {
        const { emailId } = newPayment.creditCardUserInputInfo;
        const emailIdError =
            validateRequired(emailId, errorFieldMap[fieldName]) || validateEmail(emailId);
        return emailIdError;
    }
    if (fieldName === 'phone') {
        const phone = get(newPayment, 'creditCardUserInputInfo.billingAddress.phone', '');
        const phoneError =
            validateRequired(phone, errorFieldMap[fieldName]) || validatePhoneNumber(phone);
        return phoneError;
    }
    if (fieldName === 'cvv') {
        const cvv = get(newPayment, 'creditCardUserInputInfo.cardInfo.cvv', '');
        const cardNumber = get(newPayment, 'creditCardUserInputInfo.cardInfo.number', '');
        const cardType = get(newPayment, 'creditCardUserInputInfo.cardInfo.type', '');
        const cvvError =
            validateRequired(cvv, errorFieldMap[fieldName]) ||
            validateCVV(cvv, cardNumber.replace(/\s+/g, ''), context, cardType);
        return cvvError;
    }
    const isNumberEditable = get(
        newPayment,
        'creditCardUserInputInfo.cardInfo.isNumberEditable',
        false
    );
    const value = newPayment.creditCardUserInputInfo?.cardInfo?.[fieldName];
    let error = validateRequired(value, errorFieldMap[fieldName]);
    /**
     * Should validate credit card number if newly entered or edited existing card number
     */
    if (
        !error &&
        fieldName === 'number' &&
        isNumberEditable &&
        get(newPayment, 'selectedCardInfo.number', '') !== value
    ) {
        const notFormattedCardNumber = value.replace(/[^(0-9)|*]/g, '');
        error = validateCreditCardNumber(notFormattedCardNumber, context);
    }
    return error;
}

/*
 * Validate credit card form fields
 */
export function validateCreditCardForm(newPayment, context) {
    const cardType = get(newPayment, 'creditCardUserInputInfo.cardInfo.type', '');
    const creditCardFormFieldMap = ['number'];
    if (!isEmpty(cardType)) {
        if (cardType.toLowerCase() !== 'jcp') {
            creditCardFormFieldMap.push('expMonth');
            creditCardFormFieldMap.push('expYear');
        }
        creditCardFormFieldMap.push('cvv');
    }

    /*
     * Email and phone field is required for guest user
     */
    if (!User.isUserLoggedIn()) {
        creditCardFormFieldMap.push('emailId');
        creditCardFormFieldMap.push('phone');
    }

    const errorInfo = {};
    each(creditCardFormFieldMap, (fieldName) => {
        const errorMessage = validateCreditCardFormField(newPayment, fieldName, context);
        if (errorMessage) {
            errorInfo[fieldName] = errorMessage;
        }
    });
    return errorInfo;
}

/*
 * Return error message based on credit card form input field
 */
export function validateGiftCardFormField(newGiftCardPayment, fieldName) {
    const errorFieldMap = {
        giftCardNumber: 'giftCardNumber',
        securityCode: 'securityCode',
    };
    let value = newGiftCardPayment.giftCardInfo.giftCardForm[fieldName];
    let error = validateRequired(value, errorFieldMap[fieldName]);
    value = value.replace(/[^(0-9)]/g, '');
    if (fieldName === 'giftCardNumber') {
        if (value.length < 19) {
            error = 'Enter a valid 19-digit gift card number';
        }
    } else if (value.length < 4) {
        error = 'Enter a valid 4 digit PIN';
    }
    return error;
}

/*
 * Validate credit card form fields
 */
export function validateGiftCardForm(newGiftCardPayment) {
    const giftCardFormFieldMap = ['giftCardNumber', 'securityCode'];
    const errorInfo = {};
    each(giftCardFormFieldMap, (fieldName) => {
        const errorMessage = validateGiftCardFormField(newGiftCardPayment, fieldName);
        if (errorMessage) {
            errorInfo[fieldName] = errorMessage;
        }
    });
    return errorInfo;
}

/*
 * Return error message based on Alternate Pickup Person form input field
 */
export function validateAlternatePickupPersonFormField(value, fieldName) {
    const errorFieldMap = {
        firstName: 'firstName',
        lastName: 'lastName',
        email: 'email',
        phone: 'phone',
    };
    if (fieldName === 'email') {
        const email = value;
        const emailIdError =
            validateRequired(email, errorFieldMap[fieldName]) || validateEmail(email);
        return emailIdError;
    }
    if (fieldName === 'phone') {
        const phone = value;
        const phoneError = phone && validatePhoneNumber(phone);
        return phoneError;
    }
    const error = validateRequired(value, errorFieldMap[fieldName]);
    return error;
}

/*
 * Return error message based on credit card form input field
 */
export function validateAlternatePickupFormField(pickupPersonInfo, fieldName) {
    const value = pickupPersonInfo[fieldName];
    const error = validateRequired(value, fieldName);
    return error;
}

/*
 * Validate Alternate Pickup Person form Submit
 */
export function validateAlternatePickupFormSubmit(pickupPersonInfo) {
    const alternatePickupFormFieldMap = ['firstName', 'lastName', 'email'];
    const errorInfo = {};
    each(alternatePickupFormFieldMap, (fieldName) => {
        const errorMessage = validateAlternatePickupFormField(pickupPersonInfo, fieldName);
        if (errorMessage) {
            errorInfo[fieldName] = errorMessage;
        }
    });
    if (pickupPersonInfo.email) {
        const emailError = validateEmail(pickupPersonInfo.email);
        if (emailError) {
            errorInfo.email = emailError;
        }
    }
    if (pickupPersonInfo.phone) {
        const phoneError = validatePhoneNumber(pickupPersonInfo.phone);
        if (phoneError) {
            errorInfo.phone = phoneError;
        }
    }
    return errorInfo;
}

/**
 * Return DA format errors
 *
 */
export function getDAFormError(formErrors) {
    const DAFormErrors = [];
    const keys = Object.keys(formErrors);
    each(keys, (key) => {
        DAFormErrors.push({ errorDescription: formErrors[key] });
    });
    return DAFormErrors;
}

/**
 * Return simple shipping experience enabled or not
 * @param context
 * @param orderDetails
 * @return boolean
 */
export function skipShippingMethodSelection(context, orderDetails) {
    const enableSkipShippingMethodSelections = get(
        context,
        'featureFlags.enableSkipShippingMethodSelections',
        true
    );
    const disableApollo = get(orderDetails, 'data.metadata.disableApollo', false);
    return enableSkipShippingMethodSelections && !disableApollo;
}

export function isMaxLengthPaypalServiceError(cartDetailsResponse) {
    const cartDetailExceptions = get(cartDetailsResponse, 'data.status.exceptions', []);
    const isPaypalSRVErr = {};
    /* istanbul ignore else */
    if (!isEmpty(cartDetailExceptions)) {
        return find(cartDetailExceptions, {
            errorCode: 'SRV_EMAIL_CHARACTER_LENGTH_NOT_VALID',
        });
    }
    return isPaypalSRVErr;
}

/**
 * Return store ID for fraud prevention
 * @param orderDetails
 * @param defaultStoreId
 * @return string
 */

export function getStoreNumber(orderDetails, defaultStoreId) {
    const storeShippingGroup = ShippingService.getOrderShipToStoreAddress(orderDetails);
    const bopisShippingGroup = ShippingService.getOrderBOPISInfo(orderDetails);
    if (!isEmpty(bopisShippingGroup)) {
        return bopisShippingGroup.store.id;
    }
    if (!isEmpty(storeShippingGroup)) {
        return storeShippingGroup.store.id;
    }
    return defaultStoreId;
}

export function getBnplErrorOrWarning(bnplIneligibleReason, isBnplOnOrder) {
    const errorTypeCode = [
        'BAG_CONTAINS_GIFT_CARD_PAYMENT',
        'PAYMENT_BASED_COUPON_APPLIED_ON_ORDER',
    ];
    if (errorTypeCode.indexOf(bnplIneligibleReason?.code) > -1 || isBnplOnOrder) {
        return 'error';
    }
    return 'warning';
}

export function isPaymentBasedWarningMsg(bnplIneligibleReason) {
    return bnplIneligibleReason?.code === 'PBC_INELIGIBLE_DUE_TO_PAYMENT_METHOD';
}

export function getPreferredPaymentCard(userPaymentList) {
    let paymentCardIndex = 0; // Select first card as payment card if preferred criteria isn't met
    const defaultCardIndex = findIndex(userPaymentList, { showDefaultLabel: true }); // Prefer default card first
    const jcpCard = findIndex(userPaymentList, { type: 'jcp' }); // Prefer JCP card next
    if (defaultCardIndex >= 0) {
        paymentCardIndex = defaultCardIndex;
    } else if (jcpCard >= 0) {
        paymentCardIndex = jcpCard;
    }
    return paymentCardIndex;
}
